import { useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import type { CustomerSearchInput } from '@repo/api-contract';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import CloseIcon from '@mui/icons-material/Close';
import {
  closeModal,
  searchCustomers,
} from '../../../../state/customerSearchModalSlice';
import DraggablePaper from '../../DraggablePaper';
import Table from './Table';
import { initialPageSize } from './constants';

export default function CustomerSearchModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tMIS } = useTranslation([], {
    keyPrefix: 'team.Modals.CustomersSearch',
  });

  const {
    selectedCustomers,
    multiSelect,
    openCustomerSearchModal,
    loading,
    onImport,
    onCancel,
  } = useSelector(({ customerSearchModalSlice }) => customerSearchModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
    if (onCancel) onCancel();
  };
  const importCustomers = () => {
    dispatch(closeModal());
    if (onImport) onImport(selectedCustomers);
  };

  const initialResults = async () => {
    const payload: CustomerSearchInput = {
      query: '',
      searchBy: 'companyName',
      size: initialPageSize,
      page: 0,
    };
    await dispatch(searchCustomers(payload));
  };

  useEffect(() => {
    if (!openCustomerSearchModal || team === 'init' || team === 'loading')
      return;
    const fetchData = async () => {
      await initialResults();
    };
    fetchData();
  }, [openCustomerSearchModal, team]);

  return (
    <Dialog
      open={openCustomerSearchModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMIS('Browse Customer')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height={800}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={{ height: 800, width: '100%' }}>
            <Table multiSelect={multiSelect} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            sx={{ width: buttonWidth }}
            onClick={importCustomers}
          >
            {loading ? <CircularProgress size={20} /> : tGlobal('Import')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
